import {
  changeUser,
  initialize,
  logCustomEvent,
  openSession,
  removeSubscription,
  subscribeToContentCardsUpdates,
} from "@braze/web-sdk";

import {
  IContentCards,
  IContentCardsResult,
} from "@origin-digital/event-dispatcher";
import { EnvironmentNames } from "@origin-digital/platform-enums";
import { logger } from "@origin-digital/reporting-client";

import { IBrazeInit } from "./consts/interfaces";
import { backroundRefresh, fetchContentCards } from "./contentCard/card";
import { inAppMessageInit } from "./inAppMessages";
import { fetchBrazeId, getBrazeIndexedDB, sendBrazeEvents } from "./utils/";
import { findOrCreateNewBrazeUser } from "./utils/braze";

import {
  BRAZE_ID_KEY,
  getApiKey,
  INAPP_ZINDEX,
  SDK_BASE_URL,
} from "./utils/config";
import { persistUser, syncUserWithAdobe } from "./utils/user";
import { showCPFeatureFlag } from "./featureFlag";

export interface IBrazeProps {
  environment: EnvironmentNames;
  /**
   * pathname from window.location called in setup
   */
  path: string;
  /**
   * current query string as a parameters object
   */
  parameters?: Record<string, string>;
}

let onBrazeInit: Promise<boolean> | undefined;

export const onBrazeInitFn = ({
  environment,
  path,
  parameters,
}: IBrazeProps) => {
  onBrazeInit = new Promise<boolean>((resolve) => {
    fetchBrazeId(parameters?.[BRAZE_ID_KEY])
      .then(async (brazeObj: IBrazeInit) => {
        // initialize/set user
        initialize(getApiKey(environment), {
          baseUrl: SDK_BASE_URL,
          enableLogging: environment !== "prod",
          doNotLoadFontAwesome: true,
          inAppMessageZIndex: INAPP_ZINDEX,
        });

        // In-App Messages
        inAppMessageInit(environment, path);

        // DO NOT REMOVE
        // this callback is required for cleaning up cached cards
        subscribeToContentCardsUpdates(({ cards }) => {
          logger.debug("[braze] subscribeToContentCardsUpdates", cards);
        });

        if (brazeObj.bzid == null) {
          const { bzid, created } = await findOrCreateNewBrazeUser(brazeObj);
          brazeObj.bzid = bzid;
          brazeObj.created = created;

          if (brazeObj.bzid == null) {
            resolve(false);
            return;
          }
        }

        // TEMP: quick test of FeatureFlag
        if (window.location.href.indexOf("electricity-gas/plans.html") > -1) {
          showCPFeatureFlag();
        }

        changeUser(brazeObj.bzid);
        openSession();

        // fire events/view card
        sendBrazeEvents(logCustomEvent);
        syncUserWithAdobe(brazeObj); // check existing cookies before they are re-written below
        persistUser(brazeObj, environment); // storage, cookies, etc...
        resolve(true);
        backroundRefresh();
      })
      .catch(() => {
        resolve(false);
      });
  });
};

let initUser: boolean | null = null;
export const contentCard = (
  payload: IContentCards["payload"],
  useCached?: boolean
): Promise<IContentCardsResult> => {
  if (initUser === true) {
    return fetchContentCards(payload, useCached);
  } else if (initUser === false) {
    return Promise.resolve({ cards: [], unknownUser: true });
  } else {
    // create promise for after Braze has initalized
    const onInitPromise = new Promise<IContentCardsResult>((resolve) => {
      if (onBrazeInit == null)
        throw Error("[braze]: onBrazeInit must be initalised");
      onBrazeInit.then((success) => {
        initUser = success;
        return resolve(contentCard(payload, useCached));
      });
    });
    return onInitPromise;
  }
};

// This method is introduced as a workaround for the bug exists in the Braze SDK when intilising it.
// The flow will only reach the subscription in the case of sdk is not initialised (every first call).
// Subscription to content cards returns immediately, and we use this to trigger the fetch and cached content cards
// as a result of `fetchContentCards(payload, useCached)` call above.
const onBrazeInitContentCardSubscription = async (
  payload: IContentCards["payload"]
): Promise<IContentCardsResult> => {
  const isBrazeNotInIndexDB = !(await getBrazeIndexedDB());
  const onBrazeInitPromise = await onBrazeInit;
  return new Promise<IContentCardsResult>((resolve) => {
    if (onBrazeInitPromise !== undefined && isBrazeNotInIndexDB) {
      const subscriptionId = subscribeToContentCardsUpdates(() => {
        resolve(contentCard(payload, true));
        removeSubscription(subscriptionId!);
      });
    }
  });
};

// export for testing
export const contentCardTimeout = ({
  payload,
}: {
  payload: IContentCards["payload"];
}): IContentCards["result"] => {
  const EXISTING_USERS_DEFAULT_TIMEOUT_30s = 1000 * 30;
  const contentCardTimeOutFn = (timeout: number) =>
    new Promise<IContentCardsResult>((resolve) => {
      setTimeout(
        () =>
          resolve(
            initUser === null
              ? { cards: [], timeout: true } // if we aren't initalized return empty array[]
              : contentCard(payload, true).then((result) => ({
                  ...result,
                  timeout: true,
                }))
          ),
        payload.timeout ?? timeout
      );
    });
  return Promise.race<IContentCardsResult>([
    contentCard(payload),
    contentCardTimeOutFn(EXISTING_USERS_DEFAULT_TIMEOUT_30s),
    onBrazeInitContentCardSubscription(payload),
  ]);
};

// export for testing
export const __resetForTesting = () => {
  onBrazeInit = undefined;
  initUser = null;
};
